// frontend/src/contexts/ApplicationManagerContext.tsx
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 * 
 * AUDITORIA: Context para gerenciamento global do estado da aplicação.
 * Gerencia autenticação, projetos, permissões e ciclo de vida da app.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { projectService } from '../services/projectService';
import rbacService from '../services/rbacService';
import type { Project } from '../types/project';
import type { IUserRole } from '../types/rbac';

// =============================================
// TYPES & INTERFACES
// =============================================

type ApplicationStage =
  | 'INITIALIZING'
  | 'UNAUTHENTICATED'
  | 'AUTHENTICATED'
  | 'LOADING_PROJECTS'
  | 'LOADING_PERMISSIONS'
  | 'READY'
  | 'ERROR';

interface User {
  id: string;
  nome: string;        // Backend usa 'nome' (português)
  name: string;        // Compatibilidade com algumas APIs
  email: string;
  projectId?: string;
  avatarUrl?: string;
  avatar_url?: string; // Compatibilidade com UserProfileSection
}

interface ApplicationState {
  stage: ApplicationStage;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  projects: Project[];
  currentProject: Project | null;
  permissions: string[];
  userRoles: IUserRole[];
  retryCount: number;
}

type ApplicationAction =
  | { type: 'SET_STAGE'; stage: ApplicationStage }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_USER'; user: User }
  | { type: 'SET_PROJECTS'; projects: Project[]; currentProject?: Project | null }
  | { type: 'SET_PERMISSIONS'; permissions: string[]; roles: IUserRole[] }
  | { type: 'SELECT_PROJECT'; projectId: string }
  | { type: 'INCREMENT_RETRY' }
  | { type: 'RESET' }
  | { type: 'UPDATE_USER_AVATAR'; avatarUrl: string };

interface ApplicationManagerContextType {
  // State
  stage: ApplicationStage;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  projects: Project[];
  currentProject: Project | null;
  permissions: string[];
  userRoles: IUserRole[];

  // Actions
  initialize: () => Promise<void>;
  reset: () => void;
  retry: () => Promise<void>;
  selectProject: (projectId: string) => Promise<void>;
  updateUserAvatar: (avatarUrl: string) => void;

  // Helpers
  isReady: boolean;
  isError: boolean;
  canRetry: boolean;

  // RBAC Helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

// =============================================
// REDUCER
// =============================================

const initialState: ApplicationState = {
  stage: 'INITIALIZING',
  isLoading: false,
  error: null,
  user: null,
  projects: [],
  currentProject: null,
  permissions: [],
  userRoles: [],
  retryCount: 0
};

function applicationReducer(state: ApplicationState, action: ApplicationAction): ApplicationState {
  switch (action.type) {
    case 'SET_STAGE':
      return { ...state, stage: action.stage };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };
    
    case 'SET_ERROR':
      return { 
        ...state, 
        error: action.error, 
        stage: action.error ? 'ERROR' : state.stage,
        isLoading: false 
      };
    
    case 'SET_USER':
      return { ...state, user: action.user };
    
    case 'SET_PROJECTS':
      return { 
        ...state, 
        projects: action.projects,
        currentProject: action.currentProject || (action.projects[0] || null)
      };
    
    case 'SET_PERMISSIONS':
      return { 
        ...state, 
        permissions: action.permissions,
        userRoles: action.roles
      };
    
    case 'SELECT_PROJECT':
      const selectedProject = state.projects.find(p => p.id === action.projectId);
      return {
        ...state,
        currentProject: selectedProject || null
      };
    
    case 'INCREMENT_RETRY':
      return { ...state, retryCount: state.retryCount + 1 };
    
    case 'UPDATE_USER_AVATAR':
      return {
        ...state,
        user: state.user ? { ...state.user, avatarUrl: action.avatarUrl } : state.user
      };
    
    case 'RESET':
      return initialState;
    
    default:
      return state;
  }
}

// =============================================
// CONTEXT
// =============================================

const ApplicationManagerContext = createContext<ApplicationManagerContextType | null>(null);

export const useApplicationManager = () => {
  const context = useContext(ApplicationManagerContext);
  if (!context) {
    throw new Error('useApplicationManager must be used within ApplicationManagerProvider');
  }
  return context;
};

// =============================================
// PROVIDER
// =============================================

interface ApplicationManagerProviderProps {
  children: React.ReactNode;
}

export const ApplicationManagerProvider: React.FC<ApplicationManagerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(applicationReducer, initialState);

  // =============================================
  // INITIALIZATION LOGIC
  // =============================================

  const initialize = useCallback(async () => {
    console.log('🚀 ApplicationManager: Iniciando inicialização...');

    let user: User | null = null; // Declarar user no escopo correto
    
    try {
      dispatch({ type: 'SET_LOADING', loading: true });
      dispatch({ type: 'SET_ERROR', error: null });

      // STEP 1: Verificar se está autenticado e buscar dados atualizados
      console.log('📌 Step 1: Verificando autenticação...');
      dispatch({ type: 'SET_STAGE', stage: 'INITIALIZING' });
      
      // Buscar dados atualizados do servidor ao invés de usar cache
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Falha na autenticação');
        }

        const data = await response.json();
        if (!data.success || !data.user) {
          throw new Error('Dados do usuário inválidos');
        }

        user = {
          id: data.user.id || data.user.userId,
          nome: data.user.nome || data.user.name || data.user.nomeCompleto,
          name: data.user.name || data.user.nome || 'Usuário',
          email: data.user.email,
          projectId: data.user.projectId,
          avatar_url: data.user.avatar_url || data.user.avatarUrl
        };

        console.log('✅ Usuário autenticado com dados atualizados:', user.email);
        dispatch({ type: 'SET_USER', user });
        dispatch({ type: 'SET_STAGE', stage: 'AUTHENTICATED' });
        
        // Atualizar cache com dados corretos
        authService.updateUserData(user);
        
      } catch (error) {
        console.log('❌ Erro na autenticação:', error);
        authService.clearAuthData();
        dispatch({ type: 'SET_STAGE', stage: 'UNAUTHENTICATED' });
        dispatch({ type: 'SET_LOADING', loading: false });
        return;
      }

      // STEP 2: Carregar projetos
      console.log('📌 Step 2: Carregando projetos...');
      dispatch({ type: 'SET_STAGE', stage: 'LOADING_PROJECTS' });
      
      const projects = await projectService.getProjects();
      
      // Try to restore previously selected project from localStorage
      const savedProjectId = localStorage.getItem('selected_project_id');
      let currentProject = null;
      
      if (savedProjectId) {
        currentProject = projects.find(p => p.id === savedProjectId);
        console.log('🔄 Projeto previamente selecionado restaurado:', currentProject?.nome || 'não encontrado');
      }
      
      // Fallback to first project if no saved selection or project not found
      if (!currentProject && projects.length > 0) {
        currentProject = projects[0];
        console.log('📌 Usando primeiro projeto como padrão:', currentProject.nome);
      }
      
      console.log(`✅ Projetos carregados: ${projects.length}`);
      dispatch({ type: 'SET_PROJECTS', projects, currentProject });

      // STEP 3: Carregar permissões RBAC (sempre executar)
      console.log('📌 Step 3: Carregando permissões RBAC...');
      dispatch({ type: 'SET_STAGE', stage: 'LOADING_PERMISSIONS' });
      
      const [permissionsResult, userRoles] = await Promise.all([
        currentProject 
          ? rbacService.getUserPermissions(user.id, currentProject.id)
          : Promise.resolve({ permissions: [] }),
        rbacService.getUserRoles(user.id, { incluirPapel: true })
      ]);

      const permissions = permissionsResult?.permissions || [];
      const roles = userRoles || [];

      // Converter permissões para array de strings
      const permissionStrings = permissions.map((p: string | { nome?: string; name?: string }) =>
        typeof p === 'string' ? p : p.nome || p.name || String(p)
      );

      console.log(`✅ Permissões carregadas: ${permissionStrings.length}`);
      console.log('🔍 [APPLICATION MANAGER] Papéis do usuário:', roles);
      dispatch({ type: 'SET_PERMISSIONS', permissions: permissionStrings, roles });

      // STEP 4: Finalizar inicialização
      console.log('✅ Inicialização completa!');
      dispatch({ type: 'SET_STAGE', stage: 'READY' });
      dispatch({ type: 'SET_LOADING', loading: false });

    } catch (error) {
      console.error('❌ Erro durante inicialização:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      });
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, []);

  const reset = useCallback(() => {
    console.log('🔄 ApplicationManager: Resetando estado...');
    dispatch({ type: 'RESET' });
  }, []);

  const retry = useCallback(async () => {
    if (state.retryCount >= 3) {
      console.log('❌ Limite de tentativas atingido');
      return;
    }

    console.log(`🔄 ApplicationManager: Tentativa ${state.retryCount + 1}/3`);
    dispatch({ type: 'INCREMENT_RETRY' });
    await initialize();
  }, [state.retryCount, initialize]);

  const selectProject = useCallback(async (projectId: string) => {
    console.log('🔄 ApplicationManager: Selecionando projeto:', projectId);

    try {
      // Chamar endpoint para regenerar JWT com novo projectId
      await authService.switchProject(projectId);

      // Atualizar state local
      dispatch({ type: 'SELECT_PROJECT', projectId });

      // Save to localStorage for persistence
      localStorage.setItem('selected_project_id', projectId);

      console.log('✅ Projeto trocado e JWT regenerado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao trocar projeto:', error);
      // Não fazer nada - manter projeto atual
      throw error;
    }
  }, []);

  const updateUserAvatar = useCallback((avatarUrl: string) => {
    dispatch({ type: 'UPDATE_USER_AVATAR', avatarUrl });
  }, []);

  // =============================================
  // EFFECTS
  // =============================================

  // Inicialização automática apenas uma vez
  useEffect(() => {
    const currentPath = window.location.pathname;
    const publicPaths = ['/login', '/register', '/register-full', '/', '/forgot-password', '/invite'];
    
    if (publicPaths.includes(currentPath)) {
      dispatch({ type: 'SET_STAGE', stage: 'UNAUTHENTICATED' });
      dispatch({ type: 'SET_LOADING', loading: false });
      return;
    }

    if (state.stage === 'INITIALIZING') {
      initialize();
    }
  }, []); // Apenas uma vez na montagem

  // =============================================
  // RBAC HELPER FUNCTIONS
  // =============================================

  /**
   * Verifica se usuário tem uma permissão específica
   * @param permission Permissão a verificar (ex: 'erp.products.list', 'users.invite')
   * @returns boolean
   */
  const hasPermission = useCallback((permission: string): boolean => {
    if (!state.permissions || state.permissions.length === 0) {
      return false;
    }
    // Super admin tem todas as permissões
    if (state.permissions.includes('*')) {
      return true;
    }
    // Check exact match ou wildcard match (ex: 'erp.*' matches 'erp.products.list')
    return state.permissions.some(p => {
      if (p === permission) return true;
      if (p.endsWith('.*')) {
        const prefix = p.slice(0, -1); // Remove '*'
        return permission.startsWith(prefix);
      }
      return false;
    });
  }, [state.permissions]);

  /**
   * Verifica se usuário tem um papel específico
   * @param role Papel a verificar (ex: 'super_admin', 'ERP_ADMIN')
   * @returns boolean
   */
  const hasRole = useCallback((role: string): boolean => {
    if (!state.userRoles || state.userRoles.length === 0) {
      return false;
    }
    return state.userRoles.some(ur => ur.papel?.nome === role);
  }, [state.userRoles]);

  /**
   * Verifica se usuário tem pelo menos uma das permissões listadas
   * @param permissions Lista de permissões (basta ter uma)
   * @returns boolean
   */
  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) {
      return false;
    }
    return permissions.some(perm => hasPermission(perm));
  }, [hasPermission]);

  // =============================================
  // CONTEXT VALUE
  // =============================================

  const contextValue: ApplicationManagerContextType = {
    // State
    stage: state.stage,
    isLoading: state.isLoading,
    error: state.error,
    user: state.user,
    projects: state.projects,
    currentProject: state.currentProject,
    permissions: state.permissions,
    userRoles: state.userRoles,

    // Actions
    initialize,
    reset,
    retry,
    selectProject,
    updateUserAvatar,

    // Helpers
    isReady: state.stage === 'READY',
    isError: state.stage === 'ERROR',
    canRetry: state.retryCount < 3,

    // RBAC Helpers
    hasPermission,
    hasRole,
    hasAnyPermission
  };

  return (
    <ApplicationManagerContext.Provider value={contextValue}>
      {children}
    </ApplicationManagerContext.Provider>
  );
};

export default ApplicationManagerContext;